<template lang="pug">
  .custom-file-upload
    terms-and-conditions-modal(v-if="showTermsAndConditions" @accept="checkTerms(true)" @close="showTermsAndConditions = false")
      template(#top)
        slot(name="top")
      template(#bottom)
        slot(name="bottom")
    .uploads(v-if="fileOpts.length > 0 && !fileUploadLoading && showCurrentAndRecent")
      .title
        b Current & Recent Uploads
        span.clear(v-if="!hasSlot" @click="clear") Clear Uploads
      Swatches.img-choice(
        :options="fileOpts"
        :modelValue="uploads.indexOf(selectedUpload)"
        @update:modelValue="(e) => selectImage(uploads[e], originalUploads[e])"
      )
    slot
    .drop-area
      .disclaimer(v-if="fileOpts.length === 0")
        slot(name="disclaimer")
          i Accepted image file types are .jpg and .png with a max size of 10MB. For best results, use a landscape orientation image.
        label.terms
          input(type="checkbox" :checked="termsChecked" @input="checkTerms($event.target.checked)")
          span I agree to the &nbsp;
          span.link(@click.stop.prevent="showTermsAndConditions = true") Terms and Conditions
      DragAndDropFileUpload(
        :resize="resize"
        :loading="fileUploadLoading"
        @fileUpload="uploadFile"
        linkText="Upload an Image"
        icon="misc/camera-plus"
        :disabled="!termsChecked"
        buttonText="Upload New Image"
        :accept="multiColor ? 'image/jpg, image/jpeg, image/png, image/svg+xml' : 'image/jpg, image/jpeg, image/png'"
        :show-button="false")
</template>
<script setup lang="ts">
import { computed, onMounted, ref, useSlots, defineAsyncComponent } from 'vue'
import {
  buildFileOptions,
  cacheFileOption,
  clearCache,
  getSVGDimensions,
  isImage,
  isSVG,
  lightOrDark,
} from '@/js/pages/product/custom/utils'

const TermsAndConditionsModal = defineAsyncComponent(() => import('../custom/terms-and-conditions-modal.vue'))

const MAX_FILE_SIZE_MB = 10
const TRANSPARENCY_PERCENTAGE_THRESHOLD = 0.4

// Mirrors the (non-exported) ResizeOptions type on sisu's DragAndDropFileUpload
interface ResizeOptions {
  width: number
  quality?: number
}

interface FileOption {
  image: string
  value: number
  fileName: string
  fileSize: number
}

const toBase64 = (file: Blob): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader()
  reader.readAsDataURL(file)
  reader.onload = () => resolve(reader.result as string)
  reader.onerror = (error) => reject(error)
})

const props = withDefaults(defineProps<{
  selectImage: (upload?: string, original?: string) => void,
  traceImage?: boolean,
  // prop to toggle current and recent uploads
  // specific ask for kneesleeves
  showCurrentAndRecent?: boolean,
  productType?: string,
  multiColor?: boolean,
  cacheKey?: string,
  resize?: ResizeOptions,
  // This controls how big the resulting SVG is
  svgHeight?: number,
  allowDuplicates?: boolean,
  // Never passed by any caller today, so traced fills render as `fill="undefined"`
  color?: string,
}>(), {
  traceImage           : true,
  showCurrentAndRecent : true,
  multiColor           : false,
  svgHeight            : 80,
  allowDuplicates      : false,
})

const slots = useSlots()

const fileOpts = ref<FileOption[]>([])
const fileUploadLoading = ref(false)
const uploads = ref<(string | undefined)[]>([])
const originalUploads = ref<string[]>([])
const selectedUpload = ref<number | null>(null)
const termsChecked = ref(false)
const showTermsAndConditions = ref(false)

const hasSlot = computed(() => !!slots.default)

function checkTerms(accepted: boolean | string = true) {
  if (accepted) {
    termsChecked.value = true
    showTermsAndConditions.value = false
    window.localStorage.setItem('upload-terms', 'true')
  } else {
    termsChecked.value = false
    window.localStorage.removeItem('upload-terms')
  }
}

function clear() {
  clearCache(props.cacheKey)
  fileOpts.value = []
  uploads.value = []
  originalUploads.value = []
  props.selectImage()
}

function validate(file: File) {
  // @todo what if they upload an SVG? We will need to validate it
  if ((file.size / 1024 / 1024) > MAX_FILE_SIZE_MB) {
    return false
  }
  if (isSVG(file)) {
    return true
  }
  if (!isImage(file.name)) {
    return false
  }

  if (props.allowDuplicates) {
    return true
  }

  return !fileOpts.value.find((option) => option.fileName === file.name && option.fileSize === file.size)
}

async function createTracedImage(file: File): Promise<string> {
  // don't trace the image if already and svg
  let tracedImage: string | undefined
  if (isSVG(file)) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        let svg = reader.result as string
        const template = document.createElement('template')
        // Replace with a single fill color
        if (!props.multiColor) {
          svg = svg.replace(/fill:(#[A-Fa-f0-9]+)/g, (result, value) => {
            if (value === 'none') {
              return 'none'
            }
            const brightness = lightOrDark(value)

            return `fill-opacity:"${brightness === 'dark' ? 1 : 0}"`
          })
        }
        try {
          template.innerHTML = svg
          const svgChild = template.content.querySelector('svg')!

          const { width, height } = svgChild.viewBox.baseVal

          svgChild.setAttribute('height', `${props.svgHeight}`)
          svgChild.setAttribute('width', `${width / (height / props.svgHeight)}`)
          svgChild.setAttribute('class', '')
          tracedImage = svgChild.outerHTML
        } catch (e) {
          console.warn('Error converting SVG', e)
          reject()
        }
        resolve(tracedImage as string)
      }
      reader.readAsText(file)
    })
  }
  // Tell webpack not to chunk the svg-png-convert in with the main Vendor chunk, since it is rarely used
  const { png2svg } = await import(/* webpackChunkName: "svgtopng" */'@rogue/svg-png-converter')
  // convert image to svg (actually accepts any image file, not just pngs)
  const img = await png2svg({ input: file, tracer: 'potrace', numberofcolors: 1 })
  const { height, width } = getSVGDimensions(img.content)
  // Printer doesn't allow for transparency, so replace all opacities with either 1 or 0 depending on percentage
  tracedImage = img.content.replace(/fill-opacity="(.*?)"/g, (_result, value) => {
    const updatedValue = +value < TRANSPARENCY_PERCENTAGE_THRESHOLD ? 0 : 1

    return `fill-opacity="${updatedValue}"`
  })

  const svgHeight = props.svgHeight
  // getSVGDimensions returns undefined for both when the traced SVG has no viewBox
  const svgWidth = width! / (height! / props.svgHeight)
  // remove grid background added to traced image
  tracedImage = tracedImage.replace(/<path.*><\/path>/m, '')

  return tracedImage.replace(/width="(\d+)"/, `width="${svgWidth}"`)
    .replace(/height="(\d+)"/, `height="${svgHeight}"`)
    .replace(/fill="black"/gi, `fill="${props.color}"`)
}

async function uploadFile(file: File) {
  // first create a local url for the file just uploaded
  const imageUrl = URL.createObjectURL(file)

  if (!validate(file)) {
    return
  }

  fileUploadLoading.value = true
  fileOpts.value.push({ image: imageUrl, value: fileOpts.value.length, fileName: file.name, fileSize: file.size })

  const originalImage = await toBase64(file)
  originalUploads.value.push(originalImage)

  if (props.traceImage) {
    const tracedImage = await createTracedImage(file)
    cacheFileOption(file, tracedImage, props.cacheKey)
    uploads.value.push(tracedImage)
    fileUploadLoading.value = false
    selectedUpload.value = uploads.value.length - 1
    props.selectImage(tracedImage, originalImage)
  } else {
    cacheFileOption(file, undefined, props.cacheKey)
    uploads.value.push(imageUrl)
    fileUploadLoading.value = false
    selectedUpload.value = uploads.value.length - 1
    props.selectImage(imageUrl, originalImage)
  }
}

onMounted(async () => {
  try {
    termsChecked.value = Boolean(window.localStorage.getItem('upload-terms'))
  } catch (e) {
    termsChecked.value = false
    console.log('Error with localstorage', e)
  }
  const fileOptions = buildFileOptions(props.cacheKey)
  await Promise.all(fileOptions.map(async ({ file, fileName, fileSize, trace }, i) => {
    fileOpts.value.push({ image: URL.createObjectURL(file), value: i, fileName, fileSize })
    originalUploads.value.push(await toBase64(file))
    if (props.traceImage) {
      uploads.value.push(trace)
    } else {
      uploads.value.push(URL.createObjectURL(file))
    }
  }))
})
</script>

<style lang="scss" scoped>
.title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: $space-xxs;
}
.disclaimer {
  margin-bottom: $size-xs;
}
.clear {
  margin-left: $size-xxs;
  font-size: $size-xxs;
  color: $color-blue;
  cursor: pointer;
}
.terms {
  display: block;
  margin-top: $size-xs;
}
.help {
  margin: $size-xs 0;
  b {
    display: block;
    padding-bottom: $size-xs;
    margin-bottom: $size-xs;
    border-bottom: 1px solid $color-gray-dark;
  }
}
</style>
